<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {ReferenceWidgetProps} from "@/utils/presenters/interfaces.ts";
import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import {computed, onBeforeMount, ref, watch} from "vue";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import EntityReferenceData = namaTypes.EntityReferenceData;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;

const CHIPS_SEARCH_THRESHOLD = 12;
const SEARCH_DEBOUNCE_MS = 300;

const props = defineProps<ReferenceWidgetProps>()
const chips = ref<EntityReferenceData[]>([]);
const fieldUtil = new ReferenceFieldWidgetUtil(props.presenter, props.fieldMetaData, props.defaultEntityType);
const fieldId = fieldUtil.fieldId();
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMoreChips = ref(false);
const isSearchVisible = ref(false);
const searchText = ref('');
let currentPage = 0;
let fetchSeq = 0;
let searchDebounceTimer: number | undefined;

async function searchChips(query: string, page: number): Promise<EntityReferenceData[]> {
  const referencedEntityType = fieldUtil.referencedEntityType.value;
  const contextInfo: FieldActionContextInfo = fieldUtil.presenter.createSearchContextInfo(fieldId);
  return await fieldUtil.searchFor({
    query,
    ownerEntityType: fieldUtil.presenter.entityType,
    contextInfo,
    referencedEntityType,
    fieldId,
    extraCriteria: fieldUtil.presenter.fetchCriteriaFromFilterField(referencedEntityType, fieldId),
    page,
  }) ?? [];
}

function isFullPage(items: EntityReferenceData[]): boolean {
  return items.length >= SearchingAndSuggestionUtil.SEARCH_PAGE_SIZE;
}

async function fetchChips(query = '') {
  const seq = ++fetchSeq;
  isLoading.value = true;
  try {
    const items = await searchChips(query, 0);
    if (seq !== fetchSeq) return;
    chips.value = items;
    currentPage = 0;
    hasMoreChips.value = isFullPage(items);
    if (hasMoreChips.value || items.length > CHIPS_SEARCH_THRESHOLD)
      isSearchVisible.value = true;
  } finally {
    if (seq === fetchSeq) isLoading.value = false;
  }
}

async function fetchMoreChips() {
  if (!hasMoreChips.value || isLoadingMore.value || isLoading.value)
    return;
  const seq = fetchSeq;
  isLoadingMore.value = true;
  try {
    const items = await searchChips(searchText.value ?? '', currentPage + 1);
    if (seq !== fetchSeq) return;
    const known = new Set(chips.value.map(c => c.id));
    const fresh = items.filter(c => !known.has(c.id));
    chips.value = [...chips.value, ...fresh];
    currentPage++;
    hasMoreChips.value = isFullPage(items) && fresh.length > 0;
  } finally {
    isLoadingMore.value = false;
  }
}

watch(searchText, (q) => {
  if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer);
  searchDebounceTimer = window.setTimeout(() => fetchChips(q ?? ''), SEARCH_DEBOUNCE_MS);
});

const selectedChip = computed<EntityReferenceData | null | undefined>(() => fieldUtil.fetchFieldValue());

const visibleChips = computed<EntityReferenceData[]>(() => {
  const selected = selectedChip.value;
  if (!selected?.id || chips.value.some(c => ObjectChecker.areEqual(c, selected)))
    return chips.value;
  return [selected, ...chips.value];
});

function toggleChip(chip: EntityReferenceData) {
  let valueToSelect = null;
  if (ObjectChecker.areNotEqual(fieldUtil.fetchFieldValue(), chip))
    valueToSelect = chip;
  fieldUtil.onSelect(valueToSelect)
}

function isChipSelected(chip: EntityReferenceData) {
  return ObjectChecker.areEqual(chip, fieldUtil.fetchFieldValue());
}

function chipLabel(chip: EntityReferenceData): string {
  return Translator.translateRef(chip);
}

onBeforeMount(async () => await fetchChips())
</script>

<template>
  <WidgetContainer
    :field="field" :entityType="presenter.entityType" :isSpanned="isSpanned"
    :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)" :isInsideComposite="isInsideComposite"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts">
    <template #icon>
      <NamaIcon :block="field" :presenter="presenter" class="tw-size-[22px] tw-text-[22px]"/>
    </template>
    <template v-slot:widget>
      <q-input v-if="isSearchVisible || searchText"
               v-model="searchText"
               dense outlined clearable hide-bottom-space
               autocomplete="off"
               :placeholder="Translator.translateId('search')"
               :loading="isLoading"
               class="nama-customize-dense-input nama-focused-input nama-field-input tw-mb-1"
               input-class="nama-input-class">
        <template v-slot:prepend>
          <q-icon name="search" size="18px"/>
        </template>
      </q-input>
      <div v-if="isLoading && !visibleChips.length" class="tw-flex tw-items-center tw-gap-2 tw-py-2 tw-px-2">
        <q-spinner size="16px" color="primary"/>
        <span class="tw-text-xs tw-text-black/50 dark:tw-text-white/50" v-text="Translator.translateId('loading')"/>
      </div>
      <div v-else-if="visibleChips.length"
           class="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-rounded-md tw-p-1.5"
           :class="{'tw-border tw-border-black/10 dark:tw-border-white/20': !(field as any)?.hideBorder}">
        <div v-for="chip in visibleChips"
             :key="chip.id"
             @click="toggleChip(chip)"
             class="tw-relative tw-grow tw-overflow-hidden tw-rounded-md tw-select-none
                    tw-bg-gray-600/40 hover:tw-bg-gray-600/50
                    tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-1 tw-shadow-md tw-cursor-pointer">
          <span
            :class="{'!tw-opacity-100': isChipSelected(chip)}"
            class="tw-absolute tw-inset-0 tw-opacity-[0.15] dark:tw-opacity-[0.08] -tw-z-0 tw-w-full tw-h-full tw-bg-primary-light dark:tw-bg-primary-dark tw-transition-opacity"/>
          <span
            v-text="chipLabel(chip)"
            class="tw-text-white dark:tw-text-white/90 tw-text-[12px] tw-z-[1]"/>
        </div>
        <div v-if="hasMoreChips"
             @click="fetchMoreChips()"
             class="tw-grow tw-rounded-md tw-select-none tw-border tw-border-dashed tw-border-primary-light/60 dark:tw-border-primary-dark/60
                    hover:tw-bg-gray-600/20 tw-px-2 tw-py-1 tw-flex tw-items-center tw-justify-center tw-gap-1 tw-cursor-pointer">
          <q-spinner v-if="isLoadingMore" size="12px" color="primary"/>
          <q-icon v-else name="expand_more" size="14px" class="tw-text-primary-light dark:tw-text-primary-dark"/>
          <span class="tw-text-primary-light dark:tw-text-primary-dark tw-text-[12px]"
                v-text="Translator.translateId('showMoreOptions')"/>
        </div>
      </div>
      <div v-else class="tw-py-2 tw-px-2">
        <span class="tw-text-xs tw-text-black/50 dark:tw-text-white/50" v-text="Translator.translateId('noResults')"/>
      </div>
    </template>
  </WidgetContainer>
</template>
